import { AmountFormData } from '@pages/Investment/Amount/Amount.types';
import { PersonalInformationFormData } from '@pages/Investment/PersonalInformation/PersonalInformation.types';
import {
  alphabetNumberSpaceRegex,
  alphabetWithSeperatorType,
  nameRegex,
  alphabetNumberRegex,
  PEPStringRegex,
  phoneRegex,
} from '@utils/validation/consts';
import { ContactDetailsFormData } from '@pages/Investment/ContactDetails/ContactDetails.types';
import * as yup from 'yup';
import { BankDetailsFormData } from '@pages/Investment/BankDetails/BankDetails.types';
import { TaxDetailsFormData } from '@pages/Investment/TaxDetails/TaxDetails.types';
import { NewUltimateBeneficialOwner } from '@features/api/request/UltimateBeneficialOwner';
import { CompanyFormData } from '@pages/Investment/Company/Company.types';
import { CompanyAuthorizedSignatoriesFormData } from '@pages/Investment/CompanyAuthorizedSignatories/CompanyAuthorizedSignatories.types';
import { CompanyAuthorizedSignatory } from '@features/api/request/CompanyAuthorizedSignatory';

yup.setLocale({
  mixed: {
    required: ({ path }) => `investment.errors.${path}.required`,
    notType: ({ path }) => `investment.errors.${path}.matches`,
  },
  string: {
    matches: ({ path }) => `investment.errors.${path}.matches`,
    email: ({ path }) => `investment.errors.${path}.email`,
  },
  number: {
    min: ({ path }) => `investment.errors.${path}.min`,
    max: ({ path }) => `investment.errors.${path}.max`,
  },
});

export const investmentAmountFormSchema = yup
  .object<Record<keyof AmountFormData, yup.AnySchema>>({
    amount: yup
      .string()
      .required()
      .test({
        name: 'investment_amount_validation',
        test: (value, { createError, options }) => {
          if (!value) return false;
          const { context } = options;
          const { minimumAmount } = context as any;

          return Number(value.replace(/[^0-9.-]+/g, '')) <=
            Number(minimumAmount) - 1
            ? createError({
                message: 'investment.errors.amount.matches',
                path: 'amount',
              })
            : true;
        },
      }),
  })
  .required();

export const investmentPersonalInformationFormSchema = yup
  .object<Record<keyof PersonalInformationFormData, yup.AnySchema>>({
    cityOfBirth: yup.string().trim().required(),
    countryOfBirth: yup.string().trim().required(),
    pep: yup.boolean().required(),
    pepDetails: yup.string().when('pep', {
      is: (val: boolean) => val,
      then: yup.string().trim().required().matches(PEPStringRegex),
      otherwise: yup.string().nullable()
    }),
  })
  .required();

export const companyDetailsFormSchema = yup
  .object<Record<keyof CompanyFormData, yup.AnySchema>>({
    name: yup.string().trim().required(),
    registrationCountry: yup.string().trim().required(),
    registrationId: yup.string().trim().required(),
    isCompanyAuthorizedRepresentative: yup.boolean(),
    isCompanyCorrespondenceEmailSame: yup.boolean(),
    contactEmail: yup
      .string()
      .trim()
      .when('isCompanyCorrespondenceEmailSame', {
        is: false,
        then: yup
          .string()
          .trim()
          .test({
            name: 'company_contact_email_validation',
            test: (value, { createError, options }) => {
              if (!value)
                return createError({
                  message: 'investment.errors.contactEmail.required',
                  path: 'contactEmail',
                });

              const { context } = options;
              const { userEmail } = context as any;

              return value === userEmail
                ? createError({
                    message: 'investment.errors.contactEmail.duplicate',
                    path: 'contactEmail',
                  })
                : true;
            },
          }),
        otherwise: yup.string().trim(),
      }),
  })
  .required();

export const ultimateBeneficialOwnerFormSchema = yup
  .object<Record<keyof NewUltimateBeneficialOwner, yup.AnySchema>>({
    email: yup.string().trim().email().required(),
    firstName: yup.string().trim().required(),
    lastName: yup.string().trim().required(),
    middleName: yup.string().trim(),
    ownershipPercentage: yup.number().min(25).max(100).required(),
    position: yup.string().required(),
  })
  .required();

export const companyAuthorizedSignatoriesFormSchema = yup
  .object<Record<keyof CompanyAuthorizedSignatoriesFormData, yup.AnySchema>>({
    title: yup.string().trim().required(),
    isThereAdditionalSignatory: yup.boolean(),
    additionalSignatory: yup.object().when('isThereAdditionalSignatory', {
      is: true,
      then: yup
        .object<
          Record<
            keyof Omit<CompanyAuthorizedSignatory, 'id' | 'isPrimary'>,
            yup.AnySchema
          >
        >({
          firstName: yup.string().trim().required(),
          middleName: yup.string().trim(),
          lastName: yup.string().trim().required(),
          email: yup.string().trim().email().required(),
          title: yup.string().trim().required(),
        })
        .required(),
      otherwise: yup.object().notRequired(),
    }),
  })
  .required();

export const taxDetailsFormSchema = yup
  .object<Record<keyof TaxDetailsFormData, yup.AnySchema>>({
    taxResidenceCountry: yup.string().trim().required(),
    primaryTin: yup.string().when('noPrimaryIdentificationNumber', {
      is: (val: string) => !val,
      then: yup.string().trim().required(),
      otherwise: yup.string().notRequired(),
    }),
    soleTaxResidence: yup.boolean().required(),
    usTaxPayer: yup.boolean().required(),
    secondaryTaxResidenceCountry: yup.string().when('soleTaxResidence', {
      is: (val: string) => !val,
      then: yup.string().trim().required(),
      otherwise: yup.string().nullable()
    }),
    secondaryTin: yup
      .string()
      .when(['soleTaxResidence', 'noSecondaryIdentificationNumber'], {
        is: (
          soleTaxResidence: boolean,
          noSecondaryIdentificationNumber: boolean
        ) => !soleTaxResidence && !noSecondaryIdentificationNumber,
        then: yup.string().trim().required(),
        otherwise: yup.string().nullable()
      }),
    noPrimaryIdentificationNumber: yup.boolean(),
    noPrimaryTinReason: yup.string().when('noPrimaryIdentificationNumber', {
      is: (val: boolean) => val,
      then: yup.string().trim().required(),
      otherwise: yup.string().notRequired(),
    }),
    noSecondaryIdentificationNumber: yup.boolean(),
    noSecondaryTinReason: yup.string().when('noSecondaryIdentificationNumber', {
      is: (val: boolean) => val,
      then: yup.string().trim().required(),
      otherwise: yup.string().nullable()
    }),
  })
  .required();

export const contactDetailsFormSchema = yup
  .object<Record<keyof ContactDetailsFormData, yup.AnySchema>>({
    streetAddress: yup.string().trim().required(),
    city: yup.string().trim().required(),
    state: yup.string().trim().required(),
    zipCode: yup.string().trim().required(),
    country: yup.string().trim().required(),
    phoneCountryCode: yup.string().trim().required(),
    phoneNumber: yup.string().trim().required().matches(phoneRegex),
  })
  .required();

export const bankDetailsFormSchema = yup
  .object<Record<keyof BankDetailsFormData, yup.AnySchema>>()
  .shape(
    {
      accountHolderName: yup.string().trim().required().matches(nameRegex),
      bankName: yup.string().trim().required().matches(nameRegex),
      bankLocation: yup
        .string()
        .trim()
        .required()
        .matches(alphabetWithSeperatorType),
      isNominee: yup.boolean().required(),
      accountNumber: yup.string().when('iban', {
        is: (iban: boolean) => !iban,
        then: yup.string().trim().required().matches(alphabetNumberSpaceRegex),
        otherwise: yup.string().notRequired(),
      }),
      swiftOrBic: yup.string().when('iban', {
        is: (iban: boolean) => !iban,
        then: yup
          .string()
          .trim()
          .required()
          .uppercase()
          .matches(alphabetNumberRegex),
        otherwise: yup.string().notRequired(),
      }),
      iban: yup.string().when('accountNumber', {
        is: (accountNumber: boolean) => !accountNumber,
        then: yup
          .string()
          .trim()
          .required()
          .uppercase()
          .matches(alphabetNumberSpaceRegex),
        otherwise: yup.string().notRequired(),
      }),
    },
    [
      ['accountNumber', 'iban'],
      ['iban', 'accountNumber'],
    ]
  )
  .required();
